import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Checkbox,
    Typography,
    Alert,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import { checkMonthBoundary } from '../../utils/dateUtils';

const HoursApprovalDialog = ({ 
    open, 
    onClose, 
    timecard,
    onSave
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [days, setDays] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [monthBoundaryInfo, setMonthBoundaryInfo] = useState(null);

    // Helper function to parse UTC date string
    const parseUTCDate = (dateString) => {
        const date = new Date(dateString);
        return new Date(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            0, 0, 0, 0
        );
    };

    // Initialize days when timecard changes or dialog opens
    useEffect(() => {
        if (timecard && open) {
            console.log('HoursApprovalDialog - Timecard data:', {
                startDate: timecard.startDate,
                endDate: timecard.endDate,
                virtual: timecard.virtual,
                days: timecard.days.map(d => ({
                    date: d.date,
                    hours: d.hours,
                    certified: d.certified,
                    approved: d.approved
                }))
            });
            
            // Check for month boundary using the raw date strings
            const boundaryInfo = checkMonthBoundary(
                timecard.startDate,
                timecard.endDate
            );
            console.log('Month boundary info:', boundaryInfo);
            setMonthBoundaryInfo(boundaryInfo);
            
            // Parse dates as UTC and sort
            const sortedDays = [...timecard.days]
                .map(day => ({
                    ...day,
                    date: parseUTCDate(day.date).toISOString(),
                    certified: day.certified || false,
                    approved: day.approved || false
                }))
                .sort((a, b) => 
                    parseUTCDate(a.date).getTime() - parseUTCDate(b.date).getTime()
                );

            console.log('HoursApprovalDialog - Sorted UTC days:', sortedDays.map(d => ({
                date: d.date,
                hours: d.hours,
                certified: d.certified,
                approved: d.approved
            })));
            setDays(sortedDays);
        }
    }, [timecard, open]);

    const handleApproveChange = (index, checked) => {
        const newDays = [...days];
        // Only allow approval changes for certified days
        if (!newDays[index].certified) {
            return;
        }
        newDays[index] = { 
            ...newDays[index], 
            approved: checked
        };
        setDays(newDays);
    };

    const handleApproveAll = (checked) => {
        setDays(days.map(day => ({
            ...day,
            // Only approve certified days
            approved: day.certified ? checked : day.approved
        })));
    };

    // Format date - mobile uses "MM/dd/yy" format, desktop uses "MMM d, yyyy" format
    const formatDate = (dateString) => {
        const date = parseUTCDate(dateString);
        if (isMobile) {
            return format(date, 'MM/dd/yy'); // e.g., "06/08/25"
        }
        return format(date, 'MMM d, yyyy'); // e.g., "Jun 8, 2025"
    };

    const getDayOfWeek = (dateString) => {
        const date = parseUTCDate(dateString);
        return format(date, 'EEE');
    };

    const handleSave = async () => {
        try {
            setIsSubmitting(true);
            console.log('Saving days:', days.map(d => ({
                date: d.date,
                hours: d.hours,
                certified: d.certified,
                approved: d.approved
            })));

            await onSave({ days });
        } catch (error) {
            console.error('Error saving approval:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const hasAnyCertifiedDays = days.some(day => day.certified);
    const areAllCertifiedDaysApproved = hasAnyCertifiedDays && 
        days.every(day => !day.certified || (day.certified && day.approved));
    const areSomeDaysApproved = days.some(day => day.approved);

    if (!timecard) return null;

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    bgcolor: 'background.paper',
                },
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
            }}
        >
            <DialogTitle>
                {isMobile ? (
                    <Box>
                        <Typography variant="h6" component="div">Hours Approval</Typography>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 0.5 }}>
                            {timecard.project?.name || 'Unassigned'}
                        </Typography>
                        <Typography variant="subtitle2" color="textSecondary">
                            {timecard.user ? `${timecard.user.firstName} ${timecard.user.lastName}` : 'Unknown User'}
                        </Typography>
                    </Box>
                ) : (
                    `Hours Approval - ${timecard.project?.name || 'Unassigned'} - ${timecard.user ? `${timecard.user.firstName} ${timecard.user.lastName}` : 'Unknown User'}`
                )}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">
                        Week of {format(parseUTCDate(timecard.startDate), 'MMM d')} - {format(parseUTCDate(timecard.endDate), 'MMM d, yyyy')}
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary">
                        Status: {timecard.status || 'Uncertified'}
                    </Typography>
                </Box>
                
                {/* Special message for virtual timecards */}
                {timecard.virtual && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This user has not created a timecard for this week.
                    </Alert>
                )}
                {monthBoundaryInfo?.isMonthBoundary && (() => {
                    // Check if all end-of-month days are approved
                    const endOfMonthDates = monthBoundaryInfo.endOfMonthDays;
                    // Use date substring comparison to match dates regardless of time part
                    const endOfMonthDays = days.filter(d => 
                        endOfMonthDates.some(date => date.substring(0, 10) === d.date.substring(0, 10))
                    );
                    const allEndOfMonthApproved = endOfMonthDays.every(d => 
                        d.approved
                    );

                    // Check if there are unapproved days in the new month
                    const newMonthDates = monthBoundaryInfo.newMonthDays;
                    // Use date substring comparison to match dates regardless of time part
                    const newMonthDays = days.filter(d => 
                        newMonthDates.some(date => date.substring(0, 10) === d.date.substring(0, 10))
                    );
                    // Check for any days in the new month that need approval (either not certified or not approved)
                    const hasUnapprovedNewMonthDays = newMonthDays.some(d => !d.approved);

                    console.log('Boundary week approval check:', {
                        endOfMonthDates,
                        endOfMonthDays: endOfMonthDays.map(d => ({
                            date: d.date,
                            certified: d.certified,
                            approved: d.approved
                        })),
                        allEndOfMonthApproved,
                        newMonthDates,
                        newMonthDays: newMonthDays.map(d => ({
                            date: d.date,
                            certified: d.certified,
                            approved: d.approved
                        })),
                        hasUnapprovedNewMonthDays,
                        shouldShowWarning: allEndOfMonthApproved && hasUnapprovedNewMonthDays
                    });

                    // Always show the warning if we're in a boundary week and there are certified but unapproved days
                    if (hasUnapprovedNewMonthDays) {
                        return (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                There are still remaining days to approve to complete the timecard week approval!!
                            </Alert>
                        );
                    }
                    return null;
                })()}
                <TableContainer component={Paper}>
                    <Table 
                        size="small"
                        sx={isMobile ? {
                            tableLayout: 'fixed',
                            width: '100%',
                            '& .MuiTableCell-root': {
                                padding: '4px',
                                fontSize: '0.75rem',
                            }
                        } : {}}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={isMobile ? { width: '18%', fontSize: '0.75rem', padding: '4px' } : {}}>Date</TableCell>
                                <TableCell align="center" sx={isMobile ? { width: '14%', fontSize: '0.75rem', padding: '4px' } : {}}>Hours</TableCell>
                                <TableCell sx={isMobile ? { width: '12%', fontSize: '0.75rem', padding: '4px' } : {}}>{isMobile ? 'Day' : 'Day of Week'}</TableCell>
                                <TableCell sx={isMobile ? { width: '26%', fontSize: '0.75rem', padding: '4px' } : {}}>Comments</TableCell>
                                <TableCell align="center" sx={isMobile ? { width: '15%', fontSize: '0.75rem', padding: '4px' } : {}}>Cert</TableCell>
                                <TableCell align="center" sx={isMobile ? { 
                                    width: '15%', 
                                    fontSize: '0.75rem', 
                                    padding: '4px',
                                    verticalAlign: 'top'
                                } : {}}>
                                    <Box sx={{ 
                                        display: 'inline-block',
                                        verticalAlign: 'baseline',
                                        lineHeight: 1
                                    }}>
                                        {isMobile ? 'App' : 'Approve'}
                                    </Box>
                                    <br />
                                    <Checkbox
                                        checked={areAllCertifiedDaysApproved}
                                        indeterminate={areSomeDaysApproved && !areAllCertifiedDaysApproved}
                                        onChange={(e) => handleApproveAll(e.target.checked)}
                                        disabled={!hasAnyCertifiedDays}
                                        sx={isMobile ? { 
                                            padding: '2px',
                                            minWidth: '32px',
                                            minHeight: '32px',
                                            '& .MuiSvgIcon-root': {
                                                fontSize: '1rem'
                                            }
                                        } : { padding: '0 0 0 4px' }}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {days.map((day, index) => (
                                <TableRow 
                                    key={day.date}
                                    sx={{
                                        bgcolor: !day.certified ? 'action.hover' : 'inherit',
                                        // Add visual indicator for month boundary days
                                        ...(monthBoundaryInfo?.isMonthBoundary && {
                                            borderLeft: (theme) => {
                                                const isEndOfMonth = monthBoundaryInfo.endOfMonthDays.some(
                                                    date => date.substring(0, 10) === day.date.substring(0, 10)
                                                );
                                                const isNewMonth = monthBoundaryInfo.newMonthDays.some(
                                                    date => date.substring(0, 10) === day.date.substring(0, 10)
                                                );
                                                if (isEndOfMonth) {
                                                    return `4px solid ${theme.palette.warning.main}`;
                                                } else if (isNewMonth) {
                                                    return `4px solid ${theme.palette.info.main}`;
                                                }
                                                return 'none';
                                            }
                                        })
                                    }}
                                >
                                    <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '18%' } : {}}>{formatDate(day.date)}</TableCell>
                                    <TableCell align="center" sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '14%' } : {}}>{parseFloat(day.hours || 0).toFixed(2)}</TableCell>
                                    <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '12%' } : {}}>{getDayOfWeek(day.date)}</TableCell>
                                    <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '26%' } : {}}>{day.comments || ''}</TableCell>
                                    <TableCell align="center" sx={isMobile ? { padding: '4px', width: '15%' } : {}}>
                                        <Checkbox
                                            checked={day.certified || false}
                                            disabled={true}
                                            sx={isMobile ? { 
                                                padding: '2px',
                                                minWidth: '32px',
                                                minHeight: '32px',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '1rem'
                                                }
                                            } : { padding: '4px' }}
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={isMobile ? { padding: '4px', width: '15%' } : {}}>
                                        <Checkbox
                                            checked={day.approved}
                                            onChange={(e) => handleApproveChange(index, e.target.checked)}
                                            disabled={!day.certified}
                                            sx={isMobile ? { 
                                                padding: '2px',
                                                minWidth: '32px',
                                                minHeight: '32px',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '1rem'
                                                }
                                            } : { padding: '4px' }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {/* Total row */}
                            <TableRow sx={{ 
                                borderTop: '2px solid',
                                borderTopColor: 'divider',
                                bgcolor: '#fff3cd'
                            }}>
                                <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '18%' } : {}}>Total</TableCell>
                                <TableCell align="center" sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '14%' } : {}}>
                                    <Box sx={{ 
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minHeight: isMobile ? '32px' : '40px',
                                        fontSize: isMobile ? '0.75rem' : '1rem',
                                        fontWeight: 'bold'
                                    }}>
                                        {days.reduce((sum, day) => sum + parseFloat(day.hours || 0), 0).toFixed(2)}
                                    </Box>
                                </TableCell>
                                <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '12%' } : {}}></TableCell>
                                <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '26%' } : {}}></TableCell>
                                <TableCell sx={isMobile ? { padding: '4px', width: '15%' } : {}}></TableCell>
                                <TableCell sx={isMobile ? { padding: '4px', width: '15%' } : {}}></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'flex-end' }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    color="primary"
                    disabled={isSubmitting || timecard.virtual}
                >
                    {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HoursApprovalDialog;
